/**
 * Reads a USB keyboard-wedge barcode scanner.
 *
 * The scanner is just a keyboard: it types the badge code and presses Enter. So
 * is the student leaning on the keyboard. Two tests separate them, and a
 * submission needs both:
 *
 *   1. the buffer matches the badge format, and
 *   2. the mean interval between keystrokes is under `maxMeanIntervalMs`.
 *
 * Wedge scanners emit well under 15 ms per character; a person cannot type
 * twelve characters at that rate. Anything failing either test is dropped
 * silently — a curious student gets no reaction, which is the right amount of
 * feedback for input that was never a scan (docs/decisions.md D-012).
 */

import { useCallback, useEffect, useRef } from "react";
import { BADGE_PATTERN } from "../api/kiosk";

/** Above this gap, the next character starts a fresh capture, not a continuation. */
const RESET_GAP_MS = 400;
/** How often to re-assert focus, in case something stole it invisibly. */
const REFOCUS_INTERVAL_MS = 3000;

export interface ScannerOptions {
  onScan: (badge: string) => void;
  /**
   * Called when a buffer matched the badge format but was typed too slowly to
   * be trusted as a scan — so a struggling scanner fails loudly, not silently.
   */
  onSlowScan?: (badge: string, meanIntervalMs: number) => void;
  /**
   * Mean ms/char ceiling. USB wedge scanners emit <15; Bluetooth HID (an
   * iPhone-paired gun) commonly delivers 20–100 ms/char, so the ceiling sits
   * well above that. The 12-char format check is the real gate — this only
   * needs to reject sustained deliberate typing, which for a random ZB code
   * read off a card runs well over 200 ms/char.
   */
  maxMeanIntervalMs?: number;
  enabled?: boolean;
}

export function useScannerInput({
  onScan,
  onSlowScan,
  maxMeanIntervalMs = 150,
  enabled = true,
}: ScannerOptions) {
  const inputRef = useRef<HTMLInputElement>(null);
  const bufferRef = useRef("");
  const firstStampRef = useRef(0);
  const lastStampRef = useRef(0);

  // Kept in refs so the document-level listener never needs re-binding when
  // the handler identities change — rebinding mid-scan would drop characters.
  const onSlowScanRef = useRef(onSlowScan);
  useEffect(() => {
    onSlowScanRef.current = onSlowScan;
  }, [onSlowScan]);
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const reset = useCallback(() => {
    bufferRef.current = "";
    firstStampRef.current = 0;
    lastStampRef.current = 0;
  }, []);

  const focus = useCallback(() => {
    const element = inputRef.current;
    if (!element) return;
    if (document.activeElement === element) return;
    // A staff member may be typing in a real field (setup, badge search).
    // Don't yank focus out from under them.
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      (active.tagName === "INPUT" ||
        active.tagName === "SELECT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable)
    ) {
      return;
    }
    element.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const now = performance.now();
      const gap = lastStampRef.current ? now - lastStampRef.current : 0;

      if (event.key === "Enter") {
        // Only an Enter that ends a capture is the scanner's. With an empty
        // buffer it is a person pressing Enter on whatever has focus (a door
        // button on the chooser, say), and that must keep working.
        if (!bufferRef.current) return;
        event.preventDefault();
        const badge = bufferRef.current;
        const length = badge.length;
        const elapsed = lastStampRef.current - firstStampRef.current;
        // With <2 chars there is no interval to measure; such a buffer can
        // never match the 12-char pattern anyway, so it fails below.
        const meanInterval =
          length > 1 ? elapsed / (length - 1) : Number.POSITIVE_INFINITY;
        reset();

        if (BADGE_PATTERN.test(badge)) {
          if (meanInterval < maxMeanIntervalMs) {
            onScanRef.current(badge);
          } else {
            // A real-looking code, typed too slowly: almost always a scanner
            // delivering keystrokes slower than expected (Bluetooth HID),
            // not a person. Surface it so the failure isn't invisible.
            onSlowScanRef.current?.(badge, meanInterval);
          }
        }
        return;
      }

      // Ignore modifiers, arrows, F-keys — anything that isn't one character.
      if (event.key.length !== 1) return;

      if (gap > RESET_GAP_MS) reset();

      // A scanner never emits more than the code before its Enter. A runaway
      // buffer means stray typing; cap it so it cannot grow without bound.
      if (bufferRef.current.length >= 64) reset();

      if (!bufferRef.current) firstStampRef.current = now;
      bufferRef.current += event.key;
      lastStampRef.current = now;
    }

    // Listening on the document, not the input, so a scan still registers in
    // the instant between focus being stolen and the refocus timer firing.
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, maxMeanIntervalMs, reset]);

  // Focus custody: on mount, on any tap, on window focus, and on a slow timer
  // as a backstop for whatever we didn't think of.
  useEffect(() => {
    if (!enabled) return;
    focus();

    const onPointer = () => window.setTimeout(focus, 0);
    document.addEventListener("pointerdown", onPointer);
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    const timer = window.setInterval(focus, REFOCUS_INTERVAL_MS);

    return () => {
      document.removeEventListener("pointerdown", onPointer);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
      window.clearInterval(timer);
    };
  }, [enabled, focus]);

  return { inputRef, refocus: focus };
}
